import { forwardRef } from 'react';
import { cn } from '../utils/cn';

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  width?: string | number;
  height?: string | number;
  shape?: 'text' | 'block' | 'circle';
  lines?: number;
}

/**
 * A placeholder sized to the content it replaces. Skeletons are `aria-hidden`
 * because the surrounding region announces its own busy state — two
 * announcements for one wait is worse than none.
 */
export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(function Skeleton(
  { width, height, shape = 'block', lines = 1, className, style, ...rest },
  ref,
) {
  const base = cn(
    'animate-pvi-shimmer bg-[linear-gradient(90deg,var(--color-surface-sunken)_0%,var(--color-surface-hover)_50%,var(--color-surface-sunken)_100%)] bg-[length:480px_100%]',
    shape === 'circle' ? 'rounded-full' : shape === 'text' ? 'rounded-sm' : 'rounded-md',
  );

  if (shape === 'text' && lines > 1) {
    return (
      <div
        ref={ref}
        aria-hidden="true"
        className={cn('flex flex-col gap-1.5', className)}
        {...rest}
      >
        {Array.from({ length: lines }).map((_, index) => (
          <div
            key={index}
            className={cn(base, 'h-3')}
            style={{ width: index === lines - 1 ? '65%' : '100%' }}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      aria-hidden="true"
      className={cn(base, shape === 'text' && 'h-3', className)}
      style={{ width, height, ...style }}
      {...rest}
    />
  );
});
